<script setup lang="ts">
//#region Import

import { onMounted, onUnmounted, ref } from 'vue';
import axios from 'axios';
import io, { Socket } from 'socket.io-client';
import { v4 as uuidv4 } from 'uuid';

// Model
import type { DefaultEventsMap } from '@socket.io/component-emitter';
import type { VistoriaEtapas } from '@/model/vistoria-etapas.interface';
import type { VistoriaFilesAndData } from '@/model/vistoria-files-data.interface';

// Component
import HeaderSimple from '@/shared/component/header/header-simple.vue';
import IconArrowLeft from '@/shared/icons/IconArrowLeft.vue';
import Loading from '@/shared/component/show-loading.vue';
import Warning from '@/shared/component/notify-warning.vue';

// Service
import { deleteDatabase } from '@/service/db';
import { getVistoriaEtapaDadosCnhNotSync } from '@/service/vistoria-etapas/dados-cnh';
import { getVistoriaEtapaDadosEnderecoNotSync } from '@/service/vistoria-etapas/dados-endereco';
import { getVistoriaEtapaDadosPessoaisNotSync } from '@/service/vistoria-etapas/dados-pessoais';
import { getVistoriaEtapaDadosVeiculoNotSync } from '@/service/vistoria-etapas/dados-veiculo';
import { getVistoriaEtapasDadosImplementoAgregadoNotSync } from '@/service/vistoria-etapas/dados-implemento-agregado';
import { getVistoriaEtapasTermosNotSync } from '@/service/vistoria-etapas/termos';
import {
  getVistoriaFiles,
  updateVistoriaEtapaS3,
  updateVistoriaEtapaSync,
} from '@/service/vistoria-files';
import { goHome, goResumo, goResultado } from '@/shared/utils/routeNavigate';
import { saveCountPrepared } from '@/service/vistoria';
import {
  dadosPessoaisPayload,
  dadosEnderecoPayload,
  dadosCnhPayload,
  dadosVeiculoPayload,
  implementoAgregadoPayload,
} from '@/service/vistoria/payloads';
import { useStepVistoria } from '@/stores/stepVistoria';
import {
  VERIFICATION_CODE_LENGTH,
  clearValidationToken,
  getValidationTokenHeaders,
  isValidationTokenFormatValid,
  normalizeValidationToken,
  saveValidationToken,
} from '@/service/validation-token';

// Other
import { TIPO_ETAPA_ASSINATURA } from '@/global';
import { sleep } from '@/shared/utils/sleep';

//#endregion

let socket: Socket<DefaultEventsMap, DefaultEventsMap>;

const vistoriaStore = useStepVistoria();

const loading = ref<boolean>(true);
const existData = ref<boolean>(false);
const showTokenForm = ref<boolean>(false);
const validationToken = ref('');
const validationTokenError = ref('');

const sendingFail = ref<boolean>(false);
const messageError = ref<string>();
const codeError = ref<string>();
const showButtonStatus = ref<boolean>(false);
const showButtonRetry = ref<boolean>(false);

const message = ref<string>('Aguardando conexão com o servidor...');

const progress = ref<number | string>('');
const fileLength = ref<number>(0);
let totalFileLength = 0;

let vistoriaFiles: VistoriaFilesAndData[];
let vistoriaTermos: VistoriaEtapas[];
let vistoriaDadosCNH: VistoriaEtapas | null;
let vistoriaDadosEndereco: VistoriaEtapas | null;
let vistoriaDadosImplementoAgregado: VistoriaEtapas[];
let vistoriaDadosPessoais: VistoriaEtapas | null;
let vistoriaDadosVeiculos: VistoriaEtapas | null;

let idProposta: number;
const room = uuidv4();

const validationHeaders = () =>
  getValidationTokenHeaders(
    vistoriaStore.vistoria!.chave,
    vistoriaStore.vistoria!.idProposta,
  );

const isValidationTokenError = (error: unknown) =>
  axios.isAxiosError(error) && error.response?.status === 401;

const isRetryableRequestError = (error: unknown) => {
  if (!axios.isAxiosError(error)) return false;
  if (!error.response) return true;

  const { status } = error.response;
  return status === 408 || status === 429 || status >= 500;
};

const getResponseErrorMessage = (error: unknown) => {
  if (!axios.isAxiosError(error)) return;

  return (
    error.response?.data?.error?.errorMessage ||
    error.response?.data?.errorMessage
  );
};

const createFileUploadError = (file: VistoriaFilesAndData, error: unknown) => {
  const etapa = vistoriaStore.vistoriaEtpas?.find(
    (item) => item.idEtapa === file.idEtapa,
  );
  let tipoArquivo = 'foto';

  if (file.tipo === TIPO_ETAPA_ASSINATURA) tipoArquivo = 'assinatura';
  if (file.tipo === 'VIDEO') tipoArquivo = 'vídeo';

  const identificacao = etapa?.nomeEtapa
    ? ` da etapa "${etapa.nomeEtapa}"`
    : '';
  let motivo =
    getResponseErrorMessage(error) ||
    (error instanceof Error ? error.message : undefined);

  if (axios.isAxiosError(error)) {
    const status = error.response?.status;

    if (!error.response) {
      motivo =
        'A conexão com o servidor foi interrompida. Verifique sua internet e tente novamente.';
    } else if (status === 408) {
      motivo = 'O envio demorou mais que o esperado. Tente novamente.';
    } else if (status === 413) {
      motivo = 'O arquivo é maior que o tamanho permitido.';
    } else if (status === 429) {
      motivo = 'O servidor está ocupado. Aguarde um momento e tente novamente.';
    } else if (status && status >= 500) {
      motivo =
        'O servidor apresentou uma instabilidade. Aguarde um momento e tente novamente.';
    }
  }

  motivo ||= 'Não foi possível concluir o envio. Tente novamente.';

  return new Error(
    `Não foi possível enviar a ${tipoArquivo}${identificacao}. ${motivo}`,
  );
};

/**
 * Envia os arquivos da vistoria
 */

const sendArquivos = async (value: VistoriaFilesAndData) => {
  const url = `${
    import.meta.env.VITE_URL_API
  }/vistoria/envia-arquivos-vistoria/${idProposta}?room=${room}`;
  const maxRetries = 3;
  let errorResult: unknown;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Sanitize
      const data = { ...value.data };

      const form = new FormData();
      form.append('file', value.contentFile, value.name);
      form.append('data', JSON.stringify(data));

      const res = await axios.post(url, form, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...validationHeaders(),
        },
      });

      const { urlFile } = res.data;

      if (!urlFile) {
        throw new Error(
          'Falha ao enviar a vistoria! Será necessário refazer a vistoria.',
        );
      }

      await updateVistoriaEtapaS3(value.idEtapa, value.chave, urlFile);
      return;
    } catch (error) {
      errorResult = error;

      if (
        isValidationTokenError(error) ||
        !isRetryableRequestError(error) ||
        attempt === maxRetries
      ) {
        break;
      }

      await sleep(2000 * attempt);
    }
  }

  if (isValidationTokenError(errorResult)) throw errorResult;

  console.error('Erro ao enviar arquivo da vistoria:', {
    idEtapa: value.idEtapa,
    idModeloFoto: value.data?.idModeloFoto,
    tipo: value.tipo,
    error: errorResult,
  });
  throw createFileUploadError(value, errorResult);
};

/**
 * Envia a assinatura
 */
const sendAssinatura = () => {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise<void>(async (resolve, reject) => {
    const url = `${
      import.meta.env.VITE_URL_API
    }/vistoria/envia-assinatura/${idProposta}?room=${room}`;

    const assinaturaFile = vistoriaFiles.find(
      (x) => x.tipo === TIPO_ETAPA_ASSINATURA,
    );

    if (assinaturaFile) {
      const form = new FormData();
      form.append('file', assinaturaFile.contentFile, assinaturaFile.name);

      let retries = 0;
      let success = false;
      const maxRetries = 10;
      let errorResult;

      while (retries < maxRetries && !success) {
        try {
          const res = await axios.post(url, form, {
            headers: {
              'Content-Type': 'multipart/form-data',
              ...validationHeaders(),
            },
          });

          if (res.data) {
            const { urlFile } = res.data;

            if (!urlFile) {
              return reject(
                createFileUploadError(
                  assinaturaFile,
                  new Error('A API não confirmou o recebimento do arquivo.'),
                ),
              );
            }

            await updateVistoriaEtapaS3(
              assinaturaFile.idEtapa,
              assinaturaFile.chave,
              urlFile,
            );
            return resolve();
          }
        } catch (error) {
          if (isValidationTokenError(error)) return reject(error);
          errorResult = error;
          if (
            axios.isAxiosError(error) &&
            error.response &&
            error.response.status >= 400 &&
            error.response.status < 500
          ) {
            return reject(createFileUploadError(assinaturaFile, error));
          }
          await sleep(2000);
          retries++;
        }
      }

      reject(createFileUploadError(assinaturaFile, errorResult));
    } else {
      resolve();
    }
  });
};

/**
 * Envia os termos
 */
const sendTermo = () => {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise<void>(async (resolve, reject) => {
    const url = `${import.meta.env.VITE_URL_API}/vistoria/termos`;
    const data: {
      idProposta: number;
      idModelo: number;
      createdAt?: string;
    }[] = [];

    vistoriaTermos.forEach((element) => {
      data.push({
        idProposta,
        idModelo: element.contrato!.idModelo,
        createdAt: element.contrato!.dataHoraAceite?.toISOString(),
      });
    });

    if (!data.length) {
      return resolve();
    }

    let retries = 0;
    let success = false;
    const maxRetries = 10;
    let errorResult;

    while (retries < maxRetries && !success) {
      try {
        await axios.post(url, data, { headers: validationHeaders() });

        await updateVistoriaEtapaSync(
          vistoriaStore.vistoria!.chave,
          vistoriaTermos,
        );
        return resolve();
      } catch (error) {
        if (isValidationTokenError(error)) return reject(error);
        errorResult = error;
        await sleep(2000);
        retries++;
      }
    }

    reject(errorResult);
  });
};

/**
 * Envia os dados Pessoais
 */
const sendDadosPessoais = () => {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise<void>(async (resolve, reject) => {
    const url = `${
      import.meta.env.VITE_URL_API
    }/vistoria/atualiza-dados-pessoais`;

    if (!vistoriaDadosPessoais) {
      return resolve();
    }

    let retries = 0;
    let success = false;
    const maxRetries = 30;
    let errorResult;

    while (retries < maxRetries && !success) {
      try {
        await axios.post(
          url,
          dadosPessoaisPayload(vistoriaDadosPessoais!.dadosPessoais!),
          { headers: validationHeaders() },
        );

        await updateVistoriaEtapaSync(vistoriaStore.vistoria!.chave, [
          vistoriaDadosPessoais!,
        ]);
        return resolve();
      } catch (error) {
        if (isValidationTokenError(error)) return reject(error);
        errorResult = error;
        await sleep(2000);
        retries++;
      }
    }

    reject(errorResult);
  });
};

/**
 * Envia os dados Endereço
 */
const sendDadosEndereco = () => {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise<void>(async (resolve, reject) => {
    const url = `${
      import.meta.env.VITE_URL_API
    }/vistoria/atualiza-dados-endereco`;

    if (!vistoriaDadosEndereco) {
      return resolve();
    }

    let retries = 0;
    let success = false;
    const maxRetries = 30;
    let errorResult;

    while (retries < maxRetries && !success) {
      try {
        await axios.post(
          url,
          dadosEnderecoPayload(vistoriaDadosEndereco!.dadosEndereco!),
          { headers: validationHeaders() },
        );

        await updateVistoriaEtapaSync(vistoriaStore.vistoria!.chave, [
          vistoriaDadosEndereco!,
        ]);
        return resolve();
      } catch (error) {
        if (isValidationTokenError(error)) return reject(error);
        errorResult = error;
        await sleep(2000);
        retries++;
      }
    }

    reject(errorResult);
  });
};

/**
 * Envia os dados da CNH
 */
const sendDadosCNH = () => {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise<void>(async (resolve, reject) => {
    const url = `${import.meta.env.VITE_URL_API}/vistoria/atualiza-dados-cnh`;

    if (!vistoriaDadosCNH) {
      return resolve();
    }

    let retries = 0;
    let success = false;
    const maxRetries = 30;
    let errorResult;

    while (retries < maxRetries && !success) {
      try {
        await axios.post(url, dadosCnhPayload(vistoriaDadosCNH!.dadosCNH!), {
          headers: validationHeaders(),
        });
        await updateVistoriaEtapaSync(vistoriaStore.vistoria!.chave, [
          vistoriaDadosCNH!,
        ]);
        return resolve();
      } catch (error) {
        if (isValidationTokenError(error)) return reject(error);
        errorResult = error;
        await sleep(2000);
        retries++;
      }
    }

    reject(errorResult);
  });
};

/**
 * Envia os dados do Veículo
 */
const sendDadosVeiculo = () => {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise<void>(async (resolve, reject) => {
    const url = `${
      import.meta.env.VITE_URL_API
    }/vistoria/atualiza-dados-veiculo`;

    if (!vistoriaDadosVeiculos) {
      return resolve();
    }

    let retries = 0;
    let success = false;
    const maxRetries = 30;
    let errorResult;

    while (retries < maxRetries && !success) {
      try {
        await axios.post(
          url,
          dadosVeiculoPayload(vistoriaDadosVeiculos!.dadosVeiculo!),
          { headers: validationHeaders() },
        );

        await updateVistoriaEtapaSync(vistoriaStore.vistoria!.chave, [
          vistoriaDadosVeiculos!,
        ]);
        return resolve();
      } catch (error) {
        if (isValidationTokenError(error)) return reject(error);
        errorResult = error;
        await sleep(2000);
        retries++;
      }
    }

    reject(errorResult);
  });
};

/**
 * Envia Implemento/Agregado
 */
const sendImplementoAgregado = () => {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise<void>(async (resolve, reject) => {
    const url = `${
      import.meta.env.VITE_URL_API
    }/vistoria/atualiza-implemento-agregado`;
    const data: any[] = [];

    for (const element of vistoriaDadosImplementoAgregado) {
      const dataImplementoAgregado =
        element.dadosImplemento ?? element.dadosAgregado;

      if (!dataImplementoAgregado) {
        return reject(
          new Error('Dados do implemento/agregado não encontrados'),
        );
      }
      data.push(implementoAgregadoPayload(dataImplementoAgregado));
    }

    if (!data.length) {
      return resolve();
    }

    let retries = 0;
    let success = false;
    const maxRetries = 30;
    let errorResult;

    while (retries < maxRetries && !success) {
      try {
        await axios.post(url, data, { headers: validationHeaders() });

        await updateVistoriaEtapaSync(
          vistoriaStore.vistoria!.chave,
          vistoriaDadosImplementoAgregado,
        );
        return resolve();
      } catch (error) {
        if (isValidationTokenError(error)) return reject(error);
        errorResult = error;
        await sleep(2000);
        retries++;
      }
    }

    reject(errorResult);
  });
};

/**
 * Envia a vistoria
 */
const sendVistoria = async () => {
  progress.value = '';
  message.value = 'Salvando a vistoria...';

  try {
    // A assinatura possui arquivo próprio e é enviada separadamente.
    await sendAssinatura();

    // Aguarda todas as etapas menores para preservar os sucessos individuais.
    const dataResults = await Promise.allSettled([
      sendTermo(),
      sendDadosCNH(),
      sendDadosEndereco(),
      sendDadosPessoais(),
      sendDadosVeiculo(),
      sendImplementoAgregado(),
    ]);

    const dataFailures = dataResults.filter(
      (result): result is PromiseRejectedResult => result.status === 'rejected',
    );

    if (dataFailures.length) {
      const validationFailure = dataFailures.find((result) =>
        isValidationTokenError(result.reason),
      );

      throw validationFailure?.reason ?? dataFailures[0].reason;
    }

    // Envia as Fotos/Vidoes
    const fileVistorias = vistoriaFiles.filter(
      (x) => x.tipo !== TIPO_ETAPA_ASSINATURA,
    );
    const fileFailures: Error[] = [];

    // O envio sequencial evita sobrecarregar a conexão de dispositivos móveis.
    for (const fileVistoria of fileVistorias) {
      try {
        await sendArquivos(fileVistoria);
      } catch (error) {
        if (isValidationTokenError(error)) throw error;

        fileFailures.push(
          error instanceof Error
            ? error
            : new Error('Não foi possível enviar um dos arquivos.'),
        );
      }
    }

    if (fileFailures.length === 1) {
      throw fileFailures[0];
    }

    if (fileFailures.length > 1) {
      throw new Error(
        `Os demais arquivos foram enviados. Permaneceram pendentes: ${fileFailures
          .map((error) => error.message)
          .join(' ')}`,
      );
    }

    message.value = 'Vistoria salva com sucesso!';
    progress.value = totalFileLength;

    await saveCountPrepared(vistoriaStore.vistoria!.chave, 0);

    // Redireciona para o resultado
    setTimeout(() => {
      vistoriaStore.setDoneDataSync(true);
      goResultado();
    }, 200);
  } catch (error: any) {
    if (isValidationTokenError(error)) {
      clearValidationToken(vistoriaStore.vistoria!.chave);
      validationToken.value = '';
      validationTokenError.value =
        'Código inválido ou expirado. Informe um novo código.';
      showTokenForm.value = true;
      sendingFail.value = false;
      showButtonRetry.value = false;
      showButtonStatus.value = false;
      loading.value = false;
      message.value = 'Confirme o código para salvar a vistoria';
      socket?.disconnect();
      return;
    }

    sendingFail.value = true;
    showButtonRetry.value = true;

    codeError.value = 'VBP_SEND';
    messageError.value =
      error?.response?.data?.error?.errorMessage ||
      error?.response?.data?.errorMessage ||
      error?.message ||
      String(error);

    loading.value = false;
    socket?.disconnect();

    console.error(error);
  }
};

const sanitizeTokenInput = () => {
  validationToken.value = normalizeValidationToken(validationToken.value)
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, VERIFICATION_CODE_LENGTH);
  validationTokenError.value = '';
};

const validateTokenAndSend = () => {
  validationTokenError.value = '';
  const token = normalizeValidationToken(validationToken.value);

  if (!isValidationTokenFormatValid(token)) {
    validationTokenError.value =
      'Informe um código alfanumérico de 6 caracteres.';
    return;
  }

  saveValidationToken(vistoriaStore.vistoria!.chave, token);
  validationToken.value = token;
  showTokenForm.value = false;
  loading.value = true;
  connectWebSocket();
};

/**
 * Soma o tamanho dos arquivos
 */
const sumFileLength = () => {
  vistoriaFiles.forEach((element) => {
    totalFileLength += element.size;
  });

  if (!totalFileLength) {
    totalFileLength = 100;
  }

  // "Efeito de quase completado"
  totalFileLength = Math.round(totalFileLength + totalFileLength * 0.1);
  fileLength.value = totalFileLength;
};

/**
 * Conecta ao WebSock
 */
const connectWebSocket = () => {
  socket = io(import.meta.env.VITE_URL_WEBSOCKET);

  socket.on('message', (res: { filename: string; length: number }) => {
    if (typeof progress.value !== 'number') {
      progress.value = 0;
    }

    progress.value += res.length;
  });

  socket.on('join_room_response', async () => {
    message.value = 'Tudo pronto para salvar a sua vistoria';
    sumFileLength();
    progress.value = 0;
    loading.value = false;

    if (existData.value) {
      sendVistoria();
    }
  });

  socket.on('connect_error', (error: any) => {
    sendingFail.value = true;
    codeError.value = 'VBP_SOCKET';
    messageError.value = 'Tente novamente dentro de alguns minutos!';
    showButtonRetry.value = true;

    loading.value = false;
    socket.disconnect();

    console.error(error);
  });

  socket.on('connect', () => {
    socket.emit('join_room', room);
  });
};

/**
 * Recupera os dados para envio
 */
const getData = async () => {
  const chave = vistoriaStore.vistoria!.chave;

  vistoriaFiles = await getVistoriaFiles(chave);
  vistoriaTermos = await getVistoriaEtapasTermosNotSync(chave);
  vistoriaDadosCNH = await getVistoriaEtapaDadosCnhNotSync(chave);
  vistoriaDadosEndereco = await getVistoriaEtapaDadosEnderecoNotSync(chave);
  vistoriaDadosPessoais = await getVistoriaEtapaDadosPessoaisNotSync(chave);
  vistoriaDadosVeiculos = await getVistoriaEtapaDadosVeiculoNotSync(chave);
  vistoriaDadosImplementoAgregado =
    await getVistoriaEtapasDadosImplementoAgregadoNotSync(chave);

  if (
    vistoriaFiles.length ||
    vistoriaTermos.length ||
    vistoriaDadosCNH ||
    vistoriaDadosVeiculos ||
    vistoriaDadosPessoais ||
    vistoriaDadosEndereco ||
    vistoriaDadosImplementoAgregado.length
  ) {
    existData.value = true;
  }
};

/**
 * Verifica se a proposta aceita alterações
 */
const checkPropostaAcceptChange = async () => {
  return new Promise<boolean>((resolve, reject) => {
    const url = `${import.meta.env.VITE_URL_API}/vistoria/permite-envio`;
    const data = {
      idProposta: vistoriaStore.vistoria!.idProposta,
    };

    axios
      .post(url, data, { headers: validationHeaders() })
      .then((res) => {
        const { result } = res.data;
        resolve(result);
      })
      .catch((error) => reject(error));
  });
};

/**
 * REcarregar os dados da vistoria
 */
const reloadVistoria = async () => {
  const chave = vistoriaStore.vistoria!.chave;

  await deleteDatabase(chave);
  goHome(chave);
};

onMounted(async () => {
  try {
    idProposta = vistoriaStore.vistoria!.idProposta;
    await getData();
    const result = await checkPropostaAcceptChange();

    if (!result) {
      loading.value = false;
      sendingFail.value = true;
      showButtonStatus.value = true;
      messageError.value = 'A vistoria não permite mais alterações!';
      return;
    }

    loading.value = false;
    showTokenForm.value = true;
    message.value = 'Confirme o código para salvar a vistoria';
  } catch (error) {
    loading.value = false;
    sendingFail.value = true;
    showButtonRetry.value = true;
    codeError.value = 'VBP_CHECK-ACCEPT-CHANGE';
    messageError.value = 'Tente novamente dentro de alguns minutos!';
  }
});

onUnmounted(() => {
  if (socket) {
    socket.disconnect();
  }
});
</script>

<template>
  <!-- Loading -->
  <Loading :show="loading" />

  <!-- Mensagem de Erro -->
  <Transition name="fade">
    <Warning
      v-if="sendingFail"
      message="Ops! Não foi possível salvar a vistoria."
      :subMessage="messageError"
    >
      <span class="code-error">{{ codeError }}</span>

      <div class="button-status" v-if="showButtonStatus">
        <button @click="reloadVistoria()" class="btn btn-link">
          Veja o Status da Vistoria
        </button>
      </div>

      <div class="btn-vistoria" v-if="showButtonRetry">
        <button class="btn" @click="goResumo()">
          <IconArrowLeft />
          <span>Voltar</span>
        </button>
      </div>
    </Warning>
  </Transition>

  <div class="envio-vistoria" v-if="!sendingFail">
    <HeaderSimple />

    <div class="container envio-vistoria__container">
      <form
        v-if="showTokenForm"
        class="envio-vistoria__container__token"
        @submit.prevent="validateTokenAndSend"
      >
        <h1 class="title">Código de validação</h1>
        <p>
          Informe o código alfanumérico de 6 caracteres fornecido pelo
          atendente.
        </p>

        <div class="form-group" :class="{ 'has-error': validationTokenError }">
          <label class="form-label" for="validation-token">Código</label>
          <input
            id="validation-token"
            v-model="validationToken"
            class="form-input validation-token-input"
            type="text"
            inputmode="text"
            autocomplete="one-time-code"
            :minlength="VERIFICATION_CODE_LENGTH"
            :maxlength="VERIFICATION_CODE_LENGTH"
            @input="sanitizeTokenInput"
          />
          <p v-if="validationTokenError" class="form-input-hint">
            {{ validationTokenError }}
          </p>
        </div>

        <button class="btn btn-primary" type="submit">Validar e salvar</button>
      </form>

      <div
        class="envio-vistoria__container__envio"
        v-else-if="loading || existData"
      >
        <h1 class="title">{{ message }}</h1>
        <progress
          class="progress"
          :value="progress"
          :max="fileLength"
        ></progress>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.envio-vistoria {
  height: 100%;
  display: flex;
  flex-direction: column;

  &__container {
    flex: 1;

    &__envio {
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 1.2rem;
    }

    &__token {
      width: min(100%, 24rem);
      min-height: 100%;
      margin: 0 auto;
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 1rem;

      p {
        margin: 0;
      }

      .validation-token-input {
        text-transform: uppercase;
        letter-spacing: 0.16rem;
      }

      h1 {
        margin-top: -2rem;
      }
    }
  }
}

.button-status {
  margin-top: 1.6rem;
}

.code-error {
  font-size: 0.4rem;
}
</style>
